import { useEffect, useState } from 'react';

import { getFormLayout } from '../services/cardLayouts';
import { isTokenValid } from '../utils';
import { normalizeId } from '../utils/ids';
import { onSessionChange } from '../utils/session';

/**
 * The FORM layout that applies to one card — the order and groups of the item
 * form and read view (utils/formLayout.js) — resolved on the API up the card
 * hierarchy, so a child card with none of its own gets its parent's.
 *
 * One request per card per few minutes, shared by every surface: the form,
 * the detail pane, the phone's inline detail and the card page read the same
 * answer from a module cache, so paging through a list of one card's items
 * never refetches. A save or a reset in the editor calls
 * `invalidateFormLayouts()`, which empties the cache and re-reads everywhere.
 *
 * Returns `{ layout, loaded, settled }`: `layout` is the resolved row
 * (`body.groups`, `card_id` naming the card that owns it) or null for
 * automatic; `loaded` is false only while the first answer for this card is
 * in flight AND for at most `waitMs` (FORM_LAYOUT_WAIT_MS) — past that the
 * caller renders automatic, and the layout swaps in if it still arrives
 * (`settled` says whether it has). A failure is automatic — a form layout is
 * how a form reads, never a reason it cannot. Without the bound, a layout
 * GET that never answered (a stalled proxy, a hung worker; the client sets
 * no timeout) kept the form behind its spinner forever although the card
 * and the item were both ready (review finding).
 *
 * Rejected: carrying the resolved layout in the card-definitions payload the
 * form already has. That payload is the whole library cache, loaded on
 * demand for every surface; resolving each card's chain there is a
 * cardLayouts read and a walk on every load, and a contract change in both
 * repos, to save one request that already runs in parallel with the item's.
 * The bound is what the hang needed.
 *
 * `enabled: false` (a public share page, which has no session to ask with)
 * answers `{ layout: null, loaded: true }` without a request — and so does
 * any render without a live session, rather than an anonymous request the
 * 401 interceptor would turn into a trip to the login page.
 */

const TTL_MS = 2 * 60 * 1000;
// How long a form waits for its layout before it renders automatic. Long
// enough that a healthy answer (tens of ms) is never a visible swap; short
// enough that a hung one reads as a slow form, not a broken one.
export const FORM_LAYOUT_WAIT_MS = 2500;
const cache = new Map(); // cardId -> { at, promise, value }
const listeners = new Set();

export function invalidateFormLayouts() {
	cache.clear();
	listeners.forEach(listener => listener());
}

// A layout is resolved under the caller's access (`resolved` is null where
// the card that owns the row is unreadable), so another account signing in
// on this tab starts from nothing — utils/session's rule for module caches.
// Only when someone WAS signed in: nobody -> someone has nothing to forget
// (the cache never loads without a session), and clearing then would drop
// a first read still in flight and ask twice.
onSessionChange((next, previous) => {
	if (previous) invalidateFormLayouts();
});

function peek(cardId) {
	const hit = cardId ? cache.get(cardId) : null;
	if (!hit || !('value' in hit) || Date.now() - hit.at > TTL_MS) return null;
	return hit;
}

/** The resolved form layout row for a card, or null. Never rejects. */
export function loadFormLayout(rawCardId) {
	const cardId = normalizeId(rawCardId);
	if (!cardId) return Promise.resolve(null);
	const hit = cache.get(cardId);
	if (hit && (!('value' in hit) || Date.now() - hit.at <= TTL_MS)) return hit.promise;
	const entry = { at: Date.now() };
	entry.promise = Promise.resolve()
		.then(() => getFormLayout(cardId))
		.then(({ resolved }) => (resolved && Array.isArray(resolved.body?.groups) ? resolved : null))
		.catch(() => null)
		.then(value => {
			entry.value = value;
			entry.at = Date.now();
			return value;
		});
	cache.set(cardId, entry);
	return entry.promise;
}

export default function useFormLayout(rawCardId, { enabled = true, waitMs = FORM_LAYOUT_WAIT_MS } = {}) {
	const cardId = normalizeId(rawCardId);
	const [version, setVersion] = useState(0);
	const [state, setState] = useState(() => {
		const hit = enabled ? peek(cardId) : null;
		return hit ? { cardId, layout: hit.value, loaded: true, settled: true } : { cardId, layout: null, loaded: !enabled || !cardId, settled: !enabled || !cardId };
	});

	useEffect(() => {
		const listener = () => setVersion(v => v + 1);
		listeners.add(listener);
		return () => { listeners.delete(listener); };
	}, []);

	useEffect(() => {
		if (!enabled || !cardId || !isTokenValid()) {
			setState({ cardId, layout: null, loaded: true, settled: true });
			return undefined;
		}
		const hit = peek(cardId);
		if (hit) {
			setState(prev => (prev.cardId === cardId && prev.settled && prev.layout === hit.value ? prev : { cardId, layout: hit.value, loaded: true, settled: true }));
			return undefined;
		}
		let live = true;
		setState(prev => (prev.cardId === cardId ? prev : { cardId, layout: null, loaded: false, settled: false }));
		// Past the wait, render automatic; the answer still lands below.
		const timer = setTimeout(() => {
			if (live) setState(prev => (prev.cardId === cardId && !prev.loaded ? { ...prev, loaded: true } : prev));
		}, waitMs);
		loadFormLayout(cardId).then(layout => {
			clearTimeout(timer);
			if (live) setState({ cardId, layout, loaded: true, settled: true });
		});
		return () => { live = false; clearTimeout(timer); };
	}, [cardId, enabled, version, waitMs]);

	// A card switch reads as not-yet-loaded until its own answer lands, never
	// as the previous card's layout.
	if (state.cardId !== cardId) return { layout: null, loaded: !enabled || !cardId, settled: !enabled || !cardId };
	return { layout: state.layout, loaded: state.loaded, settled: state.settled };
}
